/**
 * AdminPosTabs — open POS tabs grouped by person, with settle dialog.
 *
 * Route: /admin/pos/tabs (admin auth).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Receipt, ChevronRight, Wallet, RefreshCw, CheckCircle2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const AdminPosTabs = () => {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState('');
  const [openTab, setOpenTab] = useState(null); // {charged_to_id, name, currency}
  const [settleOpen, setSettleOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [reference, setReference] = useState('');

  const { data: tabs = [], isLoading, refetch } = useQuery({
    queryKey: ['pos-tabs', tenantId],
    queryFn: async () => {
      const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const { data } = await axios.get(`${API}/api/pos/tabs${qs}`,
                                          { headers: authH() });
      return data;
    },
    refetchInterval: 10000,
  });

  const { data: detail } = useQuery({
    queryKey: ['pos-tab-detail', openTab?.charged_to_id],
    queryFn: async () => (
      await axios.get(`${API}/api/pos/tabs/${openTab.charged_to_id}`,
                       { headers: authH() })
    ).data,
    enabled: !!openTab?.charged_to_id,
  });

  const settle = useMutation({
    mutationFn: async () => {
      await axios.post(
        `${API}/api/pos/tabs/${openTab.charged_to_id}/settle`,
        { payment_method: paymentMethod, reference: reference || null },
        { headers: authH() }
      );
    },
    onSuccess: () => {
      toast.success(`Settled — ${detail?.open_count} sales`);
      setSettleOpen(false);
      setOpenTab(null);
      setReference('');
      qc.invalidateQueries({ queryKey: ['pos-tabs'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Settle failed'),
  });

  const grandTotal = tabs.reduce((s, t) => s + (t.open_total || 0), 0);
  const currency = tabs[0]?.currency || 'USD';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Receipt className="h-6 w-6 text-indigo-400" /> POS — Open Tabs
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Sales charged to a badge / staff / member / visitor pass — settled here.
            </p>
          </div>
          <div className="flex gap-2">
            <Input value={tenantId} onChange={e => setTenantId(e.target.value)}
                   placeholder="Tenant ID (blank = all)"
                   className="w-full max-w-[220px] bg-slate-900/60 border-slate-800"
                   data-testid="pos-tabs-tenant" />
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>

        <div className="grid sm:grid-cols-3 gap-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Open tabs</div>
              <div className="text-3xl font-semibold text-white">{tabs.length}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Owed total</div>
              <div className="text-3xl font-semibold text-amber-300">
                {currency} {grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Avg per tab</div>
              <div className="text-3xl font-semibold text-indigo-300">
                {currency} {tabs.length ? (grandTotal / tabs.length).toFixed(2) : '0.00'}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm text-slate-200">All open tabs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left px-4 py-2">Person</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Sales</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2 hidden sm:table-cell">Last sale</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody data-testid="pos-tabs-table">
                {isLoading && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!isLoading && tabs.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                    No open tabs. Cashiers can open one via POS scanner → Charge to badge.
                  </td></tr>
                )}
                {tabs.map(t => (
                  <tr key={t.charged_to_id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition"
                      onClick={() => { setOpenTab(t); setSettleOpen(false); }}
                      data-testid={`pos-tab-row-${t.charged_to_id}`}>
                    <td className="px-4 py-2 text-slate-100">{t.charged_to_name || '(no name)'}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="text-[10px]">{t.charged_to_token_type}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.open_sales}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium text-amber-300">
                      {t.currency} {t.open_total.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 hidden sm:table-cell">
                      {t.last_sale_at ? new Date(t.last_sale_at).toLocaleString() : ''}
                    </td>
                    <td className="px-4 py-2"><ChevronRight className="h-4 w-4 text-slate-500" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Tab detail drawer */}
        <Dialog open={!!openTab && !settleOpen} onOpenChange={(o) => !o && setOpenTab(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl text-slate-200">
            <DialogHeader>
              <DialogTitle>{openTab?.charged_to_name || '—'}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {openTab?.charged_to_token_type} · {openTab?.open_sales} open sales
              </DialogDescription>
            </DialogHeader>
            {detail && (
              <div className="space-y-2 max-h-[400px] overflow-y-auto"
                   data-testid="pos-tab-detail">
                {detail.open_sales.map(s => (
                  <div key={s.id} className="border border-slate-800 rounded-md p-3 bg-slate-800/30">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">
                        {(s.items || []).map(i => `${i.qty}× ${i.name}`).join(' · ')}
                      </span>
                      <span className="font-medium text-amber-300 tabular-nums">
                        {s.currency} {Number(s.total).toFixed(2)}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {s.cashier_email} · {new Date(s.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="text-slate-400 text-sm">Owed</span>
                  <span className="text-2xl font-semibold text-amber-300 tabular-nums">
                    {detail.currency} {detail.open_total.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenTab(null)}>Close</Button>
              <Button onClick={() => setSettleOpen(true)} data-testid="pos-tab-settle-open">
                <Wallet className="h-4 w-4 mr-1" />Settle tab
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settle dialog */}
        <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
            <DialogHeader>
              <DialogTitle>Settle tab — {openTab?.charged_to_name}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Receives payment for all {detail?.open_count || 0} open sales ({detail?.currency} {detail?.open_total?.toFixed(2)})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Payment method</label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-slate-900 border-slate-800"
                                  data-testid="pos-tab-settle-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="mobile_money">Mobile money</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="write_off">Write off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Reference (optional)</label>
                <Input value={reference} onChange={e => setReference(e.target.value)}
                       placeholder="e.g. M-PESA code or receipt #"
                       className="bg-slate-900 border-slate-800"
                       data-testid="pos-tab-settle-ref" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSettleOpen(false)}>Cancel</Button>
              <Button onClick={() => settle.mutate()} disabled={settle.isPending}
                       data-testid="pos-tab-settle-confirm">
                {settle.isPending
                  ? 'Settling…'
                  : <><CheckCircle2 className="h-4 w-4 mr-1" />Confirm settle</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPosTabs;
